import { Flex, FlexProps, Spinner } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

type LoadingProps = {
  /**
   * `inline` - ожидание внутри чего-то: строка списка, кнопка, панель. Занимает свою полосу
   * и не двигает соседей.
   * `block`  - ожидание вместо содержимого: лента, сетка, форма. Забирает ширину и центрирует
   * спиннер в полосе фиксированной высоты, чтобы страница не прыгала, когда содержимое придёт.
   */
  variant?: 'inline' | 'block';
  /** Подпись для тех, кто читает экраном. По умолчанию - «загружается» на языке страницы. */
  label?: string;
} & Omit<FlexProps, 'children'>;

/**
 * Ожидание: одно на всё, что грузится.
 *
 * Спиннер был написан руками девять раз, и четыре из них - дословно один и тот же набор
 * (`borderWidth="4px" animationDuration="0.85s" size="xl"`) в обёртке `Flex height="60px"`
 * с центрированием: вход, регистрация, лента, сетка. Остальные пять разошлись: где-то `sm`,
 * где-то `md` с `whiteAlpha.400`, где-то `blue.solid` - цвет, которого в палитре нет вовсе.
 * Человек видел на четырёх экранах четыре разных ожидания одного и того же.
 *
 * Скелетоны сюда не входят и не должны: это другая роль. Скелетон показывает форму того, что
 * придёт, и годится, когда форма заранее известна (карточка, строка, таблица). Спиннер говорит
 * только «идёт» и нужен там, где форма неизвестна или содержимое приходит целиком.
 *
 * Высота полосы 60px у `block` - не украшение: без неё лента подпрыгивает в момент, когда
 * спиннер сменяется карточками, и глаз теряет место, на которое смотрел.
 */
export const Loading = ({ variant = 'block', label, ...rest }: LoadingProps) => {
  /*
    Подпись переводится, а не зашита по-русски. Раньше в `aria-label` стояло русское
    «Загружается» значением параметра по умолчанию: на всех девяти экранах, включая английскую
    версию, читающий экраном слышал русское слово. В словарях этой строки не было вовсе.

    Умолчание считается в теле, а не в списке параметров: параметры вычисляются до тела, и хука
    там нет. Переданную подпись `??` уважает - `PushOnboarding` передаёт свою.
  */
  const { t } = useLingui();
  const isBlock = variant === 'block';

  return (
    <Flex
      role="status"
      aria-label={label ?? t`Loading`}
      alignItems="center"
      justifyContent="center"
      {...(isBlock ? { w: '100%', height: '60px' } : {})}
      {...rest}
    >
      <Spinner
        size={isBlock ? 'xl' : 'sm'}
        borderWidth={isBlock ? '4px' : '2px'}
        animationDuration="spinner"
      />
    </Flex>
  );
};
